import LiaAgentsAPI from '../../api/liaAgents';

const state = {
  records: [],
  templates: [],
  entitlements: {
    has_access: false,
    versions: [],
    can_create_14: false,
    can_create_21: false,
    can_create_31: false,
    total_agents: 0,
    subscription_plan: null,
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingTemplates: false,
    isFetchingEntitlements: false,
  },
};

const getters = {
  getAgents: $state => $state.records,
  getTemplates: $state => $state.templates,
  getEntitlements: $state => $state.entitlements,
  getAvailableVersions: $state => $state.entitlements.versions,
  canCreateAgent: $state => version => {
    if (version === '1.0') return $state.entitlements.can_create_10;
    if (version === '1.4') return $state.entitlements.can_create_14;
    if (version === '2.1') return $state.entitlements.can_create_21;
    if (version === '3.1') return $state.entitlements.can_create_31;
    return false;
  },
  getUIFlags: $state => $state.uiFlags,
  getAgentById: $state => id => $state.records.find(agent => agent.id === id),
};

const actions = {
  async fetch({ commit }) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await LiaAgentsAPI.get();
      commit('setAgents', response.data);
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTemplates({ commit }) {
    commit('setUIFlag', { isFetchingTemplates: true });
    try {
      const response = await LiaAgentsAPI.getTemplates();
      commit('setTemplates', response.data);
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    } finally {
      commit('setUIFlag', { isFetchingTemplates: false });
    }
  },

  async fetchEntitlements({ commit }) {
    commit('setUIFlag', { isFetchingEntitlements: true });
    try {
      const response = await LiaAgentsAPI.getEntitlements();
      commit('setEntitlements', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    } finally {
      commit('setUIFlag', { isFetchingEntitlements: false });
    }
  },

  async create({ commit }, agentData) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await LiaAgentsAPI.create(agentData);
      commit('addAgent', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...agentData }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await LiaAgentsAPI.update(id, agentData);
      commit('updateAgent', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async delete({ commit }, { id, password }) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await LiaAgentsAPI.deleteWithPassword(id, password);
      commit('removeAgent', id);
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async activate({ commit }, id) {
    try {
      const response = await LiaAgentsAPI.activate(id);
      commit('updateAgent', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    }
  },

  async deactivate({ commit }, id) {
    try {
      const response = await LiaAgentsAPI.deactivate(id);
      commit('updateAgent', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message);
    }
  },
};

const mutations = {
  setAgents($state, agents) {
    $state.records = agents;
  },

  setTemplates($state, templates) {
    $state.templates = templates;
  },

  setEntitlements($state, entitlements) {
    $state.entitlements = entitlements;
  },

  addAgent($state, agent) {
    $state.records.push(agent);
  },

  updateAgent($state, updatedAgent) {
    const index = $state.records.findIndex(a => a.id === updatedAgent.id);
    if (index !== -1) {
      $state.records[index] = updatedAgent;
    }
  },

  removeAgent($state, id) {
    $state.records = $state.records.filter(a => a.id !== id);
  },

  setUIFlag($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
